import { useEffect, useRef, useState } from "react";

interface AudioPlayerProps {
  audioUrl: string;
  isUserMessage: boolean;
  messageId: string;
}

export default function AudioPlayer({ audioUrl, isUserMessage, messageId }: AudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const audioRef = useRef<HTMLAudioElement>(null);
  const playerId = `audio-player-${messageId}`;

  // Load audio metadata when component mounts
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  // Format time in MM:SS format
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Toggle play/pause
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <div className={`flex items-center ${isUserMessage ? "text-white" : "text-gray-800"}`}>
      <audio ref={audioRef} src={audioUrl} preload="metadata" id={playerId} />

      <button
        onClick={togglePlay}
        className={`flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full mr-2 transition-colors duration-200 ${
          isUserMessage ? "bg-blue-500 hover:bg-blue-400 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"
        }`}
        disabled={isLoading}
        aria-label={isPlaying ? "Pause" : "Play"}
      >
        {isLoading ? (
          <div className="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
        ) : isPlaying ? (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <rect x="6" y="4" width="4" height="16" rx="1" fill="currentColor" />
            <rect x="14" y="4" width="4" height="16" rx="1" fill="currentColor" />
          </svg>
        ) : (
          <svg className="w-4 h-4 ml-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M5 3l14 9-14 9V3z" fill="currentColor" />
          </svg>
        )}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center">
          {isPlaying && (
            <div className="flex items-end h-3 mr-2">
              <div className="equalizer-bar equalizer-bar-1" style={{ height: "3px" }}></div>
              <div className="equalizer-bar equalizer-bar-2" style={{ height: "6px" }}></div>
              <div className="equalizer-bar equalizer-bar-3" style={{ height: "9px" }}></div>
              <div className="equalizer-bar equalizer-bar-4" style={{ height: "5px" }}></div>
            </div>
          )}
          <div className="flex-1 h-1.5 bg-opacity-30 rounded-full overflow-hidden relative bg-current">
            <div
              className="h-full bg-current absolute left-0 top-0 transition-all"
              style={{ width: `${(currentTime / duration) * 100}%` }}
            />
          </div>
          <span className="ml-2 text-xs whitespace-nowrap">
            {isLoading ? "Loading..." : `${formatTime(currentTime)} / ${formatTime(duration)}`}
          </span>
        </div>
      </div>
    </div>
  );
}
